import { CssBaseline, ThemeProvider } from '@mui/material';
import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { store } from '@/app/store';
import { ErrorBoundary } from '@/components/feedback/ErrorBoundary';
import { ToastProvider } from '@/components/feedback/ToastProvider';
import { AuthBootstrap } from '@/features/auth/AuthBootstrap';
import { AppRouter } from '@/routes/AppRouter';
import { buildTheme } from '@/theme/theme';

/**
 * Theme is built inside the Provider so it can read the persisted colour mode from the store,
 * and memoised so toggling a sidebar does not rebuild the entire MUI theme object.
 */
function ThemedApp() {
  const colorMode = useAppSelector((state) => state.ui.colorMode);
  const theme = useMemo(() => buildTheme(colorMode), [colorMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <BrowserRouter>
          {/* Fires one /auth/refresh so a reload restores the memory-only session. */}
          <AuthBootstrap>
            <AppRouter />
          </AuthBootstrap>
        </BrowserRouter>
      </ToastProvider>
    </ThemeProvider>
  );
}

// Expose store in dev so integration tests and browser console can inject sessions.
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__store = store;
}

const container = document.getElementById('root');
if (!container) {
  throw new Error('#root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    {/* Outermost boundary: catches a failure in the providers themselves, where there is no
        theme or toast layer left to render a nicer message with. */}
    <ErrorBoundary>
      <Provider store={store}>
        <ThemedApp />
      </Provider>
    </ErrorBoundary>
  </StrictMode>,
);
